import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { VerifiedUser } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type { CertificateResponse } from '@/api/types';
import { toApiError } from '@/api/client';
import { formatCuit, formatDate } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/certificados/')({
  component: AdminCertificatesPage,
});

const WINDOWS = [15, 30, 60, 90];

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function urgency(days: number): { label: string; color: 'error' | 'warning' | 'default' } {
  if (days <= 0) return { label: 'Vencido', color: 'error' };
  if (days <= 7) return { label: `${days} d`, color: 'error' };
  if (days <= 15) return { label: `${days} d`, color: 'warning' };
  return { label: `${days} d`, color: 'default' };
}

function AdminCertificatesPage(): JSX.Element {
  const [days, setDays] = useState(30);

  const q = useQuery({
    queryKey: ['admin-certs-expiring', days],
    queryFn: () => adminApi.listExpiringCertificates(days),
    placeholderData: keepPreviousData,
  });

  const rows: CertificateResponse[] = q.data?.items ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedUser color="primary" /> Certificados por vencer
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Certificados ARCA activos próximos a vencer, en todos los tenants. Solo metadata.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Ventana
        </Typography>
        <Select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 130 }}>
          {WINDOWS.map((d) => (
            <MenuItem key={d} value={d}>
              Próximos {d} días
            </MenuItem>
          ))}
        </Select>
        {q.data ? (
          <Chip
            size="small"
            color={rows.length === 0 ? 'success' : 'warning'}
            label={rows.length === 0 ? 'Ninguno por vencer' : `${rows.length} por vencer`}
          />
        ) : null}
      </Stack>

      {q.isError ? (
        <Alert severity="error">{toApiError(q.error).message}</Alert>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" aria-label="Certificados por vencer">
              <TableHead>
                <TableRow>
                  <TableCell>CUIT</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Ambiente</TableCell>
                  <TableCell>Vence</TableCell>
                  <TableCell>Restan</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {q.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No hay certificados que venzan en los próximos {days} días. 🎉
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((c) => {
                    const d = daysUntil(c.notAfter);
                    const u = urgency(d);
                    return (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ fontFamily: 'var(--font-mono)' }}>{formatCuit(c.cuitOnCert)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {c.tenantId.slice(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={c.environment === 'Production' ? 'primary' : 'default'}
                            label={c.environment === 'Production' ? 'Producción' : 'Homologación'}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(c.notAfter)}</TableCell>
                        <TableCell>
                          <Chip size="small" color={u.color} label={u.label} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {c.status}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
